import React, { useState } from 'react';
import './css/ViralClipsDashboardRow.css';

// VideoPlayer component from Dashboard.jsx for consistency
const VideoPlayer = ({ src, className }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const handleError = () => {
    console.error(`Error loading video from ${src}`);
    setHasError(true);
    setIsLoading(false);
  };
  
  const handleLoad = () => {
    console.log(`Video loaded successfully: ${src}`);
    setIsLoading(false);
  };
  
  if (hasError) {
    return (
      <div className="video-error">
        <p>Unable to play video directly. <a href={src} target="_blank" rel="noopener noreferrer">Open video in new tab</a></p>
        <div className="video-placeholder">
          <span className="video-placeholder-icon">▶️</span>
        </div>
      </div>
    );
  }
  
  return (
    <>
      {isLoading && <div className="video-loading">Loading video...</div>}
      <video 
        controls 
        src={src} 
        className={className}
        onError={handleError}
        onLoadedData={handleLoad}
      />
    </>
  );
};

const ViralClipsDashboardRow = ({ clipsData, videoUrls = {} }) => {
  const [expandedClipId, setExpandedClipId] = useState(null);

  const toggleExpand = (clipId) => {
    setExpandedClipId(expandedClipId === clipId ? null : clipId);
  };

  // If you have the original video URL
  const originalVideoUrl = videoUrls.original || null;
  
  // Extract model information
  const modelInfo = clipsData?.model_information || null;

  return (
    <div className="viral-clips-dashboard">
      {/* Processing Information Dashboard */}
      {modelInfo && (
        <div className="processing-dashboard">
          <h3>Analysis Information</h3>
          <div className="processing-metrics">
            <div className="metric-item">
              <div className="metric-label">Model</div>
              <div className="metric-value">{modelInfo.model_id}</div>
            </div>
            <div className="metric-item">
              <div className="metric-label">Region</div>
              <div className="metric-value">{modelInfo.region}</div>
            </div>
            <div className="metric-item">
              <div className="metric-label">Processing Time</div>
              <div className="metric-value">{modelInfo.processing_time.toFixed(2)}s</div>
            </div>
            <div className="metric-item">
              <div className="metric-label">Processed At</div>
              <div className="metric-value">{new Date(modelInfo.processed_at).toLocaleString()}</div>
            </div>
          </div>
        </div>
      )}
      
      {originalVideoUrl && (
        <div className="original-video-section">
          <h3>Original Video</h3>
          <div className="video-container">
            <VideoPlayer 
              src={originalVideoUrl} 
              className="original-video"
            />
          </div>
        </div>
      )}
      
      <h2>Generated Clips</h2>
      
      <div className="clip-category">
        <h3>Captioned</h3>
        <div className="clips-row">
          {clipsData?.clips && clipsData.clips.map((clip) => {
            const clipVideoUrl = videoUrls[clip.id] || null;
            
            return (
              <div 
                key={clip.id} 
                className={`clip-card ${expandedClipId === clip.id ? 'expanded' : ''}`}
                onClick={() => toggleExpand(clip.id)}
              >
                {clipVideoUrl && (
                  <div className="video-container">
                    <VideoPlayer 
                      src={clipVideoUrl} 
                      className="clip-video"
                    />
                  </div>
                )}
                
                <div className="clip-header">
                  <h4>{clip.heading}</h4>
                  <div className="viral-score-badge">
                    <span>{clip.viral_score}/10</span>
                  </div>
                </div>
                
                {/* <div className="clip-caption">{clip.small_caption}</div> */}
                
                <div className="clip-metadata">
                  <div className="metadata-row">
                    <div className="metadata-label">Factors:</div>
                    <div className="metadata-content factors">
                      {clip.viral_factors.map((factor, index) => (
                        <span key={index} className="factor-tag">{factor}</span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="metadata-row">
                    <div className="metadata-label">Hashtags:</div>
                    <div className="metadata-content hashtags">
                      {clip.hashtag_categories.map((hashtag, index) => (
                        <span key={index} className="hashtag-tag">{hashtag}</span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="timing-row">
                    <span className="time-item">
                      <span className="time-label">Start:</span> {clip.start_time}
                    </span>
                    <span className="time-item">
                      <span className="time-label">End:</span> {clip.end_time}
                    </span>
                  </div>
                </div>
                
                {expandedClipId === clip.id && (
                  <div className="expanded-content">
                    <div className="expanded-section">
                      <h5>Hook Suggestions</h5>
                      <ul className="hook-list">
                        {clip.hook_suggestions.map((hook, index) => (
                          <li key={index}>{hook}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="expanded-section">
                      <h5>Engagement Prompts</h5>
                      <ul className="prompt-list">
                        {clip.audience_engagement_prompts.map((prompt, index) => (
                          <li key={index}>{prompt}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="expanded-section">
                      <h5>Text Transcript</h5>
                      <div className="clip-transcript">
                        {clip.actual_text}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ViralClipsDashboardRow; 